import React, { useEffect, useMemo, useState } from 'react'
import {
  Users,
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  Image as ImageIcon,
  Link2,
  ListFilter,
  RefreshCcw,
  Search,
  UserPlus
} from 'lucide-react'

import {
  getTeam,
  addTeamMember,
  updateTeamMember,
  deleteTeamMember
} from './api'
import { Disc } from 'lucide-react'

// Platform → FA icon mapping (used to auto-fill `icon`)
const PLATFORM_ICON_MAP = {
  LinkedIn: 'fa-linkedin',
  X: 'fa-x-twitter',
  GitHub: 'fa-github',
  Instagram: 'fa-instagram',
  WhatsApp: 'fa-whatsapp',
  YouTube: 'fa-youtube',
  Facebook: 'fa-facebook',
  TikTok: 'fa-tiktok',
  Discord: 'fa-discord',
  Telegram: 'fa-telegram',
  Snapchat: 'fa-snapchat'
}
const SOCIAL_OPTIONS = Object.keys(PLATFORM_ICON_MAP)

const classNames = (...s) => s.filter(Boolean).join(' ')

// Helper function to normalize image URLs
const normaliseImageUrl = (imageUrl) => {
  if (!imageUrl || imageUrl.trim() === '') return ''

  const trimmed = imageUrl.trim()

  // If  already starts with /images/, return as is
  if (trimmed.startsWith('/images/')) {
    return trimmed
  }

  // If it starts with a forward slash, remove it before adding /images/
  const cleanPath = trimmed.startsWith('/') ? trimmed.substring(1) : trimmed
  return `/images/${cleanPath}`
}

const TeamCrudApp = () => {
  const [team, setTeam] = useState([])
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState(null)
  const [error, setError] = useState(null)

  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('All')
  const [statusFilter, setStatusFilter] = useState('active')

  // New member state
  const [showCreate, setShowCreate] = useState(false)
  const [newMember, setNewMember] = useState({
    name: '',
    role: '',
    roles: [],
    bio: '',
    image: '',
    social: [],
    status: 'active'
  })

  // Edit map: id -> object (to allow multiple inline edits if desired)
  const [editMap, setEditMap] = useState({})

  useEffect(() => {
    loadTeam()
  }, [])

  async function loadTeam() {
    setLoading(true)
    setError(null)
    try {
      const data = await getTeam()
      setTeam(Array.isArray(data) ? data : [])
    } catch (e) {
      setError(e.message || 'Failed to load team')
    } finally {
      setLoading(false)
    }
  }

  // Derived: all unique roles
  const allRoles = useMemo(() => {
    const s = new Set()
    team.forEach((m) => (m.roles || []).forEach((r) => s.add(r)))
    return ['All', ...Array.from(s).sort()]
  }, [team])

  // Filtered list
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return team.filter((m) => {
      const matchesSearch =
        !q ||
        [m.name, m.role, m.bio, ...(m.roles || [])]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q)
      const matchesRole =
        roleFilter === 'All' || (m.roles || []).includes(roleFilter)
      const status = (m.status || 'active').toLowerCase()
      const matchesStatus =
        !statusFilter || statusFilter === 'all' || status === statusFilter
      return matchesSearch && matchesRole && matchesStatus
    })
  }, [team, search, roleFilter, statusFilter])

  // Create
  async function handleCreate() {
    if (!newMember.name || !newMember.role) {
      alert('Name and role are required')
      return
    }
    try {
      const updated = await addTeamMember({
        ...newMember,
        image: normaliseImageUrl(newMember.image),
        status: (newMember.status || 'active').toLowerCase(),
        roles: Array.isArray(newMember.roles)
          ? newMember.roles
          : String(newMember.roles)
              .split(',')
              .map((r) => r.trim())
              .filter(Boolean),
        social: (newMember.social || []).map((s) => ({
          platform: s.platform || '',
          url: s.url || '',
          icon: s.icon || PLATFORM_ICON_MAP[s.platform] || ''
        }))
      })
      setTeam(updated)
      setNewMember({
        name: '',
        role: '',
        roles: [],
        bio: '',
        image: '',
        social: [],
        status: 'active'
      })
      setShowCreate(false)
    } catch (e) {
      alert(e.message || 'Failed to add')
    }
  }

  // Begin edit
  function startEdit(member) {
    setEditMap((prev) => ({
      ...prev,
      [member.id]: JSON.parse(JSON.stringify(member))
    }))
  }

  // Cancel edit
  function cancelEdit(id) {
    setEditMap((prev) => {
      const copy = { ...prev }
      delete copy[id]
      return copy
    })
  }

  // Save edit
  async function saveEdit(id) {
    const updated = editMap[id]
    if (!updated) return
    setSavingId(id)
    try {
      const normalized = {
        ...updated,
        image: normaliseImageUrl(updated.image),
        status: (updated.status || 'active').toLowerCase(),
        roles: Array.isArray(updated.roles)
          ? updated.roles
          : String(updated.roles)
              .split(',')
              .map((r) => r.trim())
              .filter(Boolean),
        social: (updated.social || []).map((s) => ({
          platform: s.platform || '',
          url: s.url || '',
          icon: s.icon || PLATFORM_ICON_MAP[s.platform] || ''
        }))
      }
      const newTeam = await updateTeamMember(normalized)
      setTeam(newTeam)
      cancelEdit(id)
    } catch (e) {
      alert(e.message || 'Failed to save')
    } finally {
      setSavingId(null)
    }
  }

  async function removeMember(id) {
    if (!confirm('Delete this member?')) return
    try {
      const updated = await deleteTeamMember(id)
      setTeam(updated)
    } catch (e) {
      alert(e.message || 'Failed to delete')
    }
  }

  // ——— UI ———

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* Dashboard Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Title Section */}
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl shadow-lg">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Team Dashboard
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage your team members and sync with GitHub
                </p>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="flex gap-4">
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
                <div className="text-2xl font-bold text-blue-700">
                  {team.length}
                </div>
                <div className="text-xs text-blue-600 font-medium">
                  Total Members
                </div>
              </div>
              <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-4 border border-green-200">
                <div className="text-2xl font-bold text-green-700">
                  {allRoles.length - 1}
                </div>
                <div className="text-xs text-green-600 font-medium">
                  Active Roles
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
            {/* Search & Filter */}
            <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search members..."
                  className="h-10 w-72 pl-10 pr-4 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50"
                />
              </div>

              <div className="flex items-center gap-2">
                <ListFilter className="h-4 w-4 text-gray-500" />
                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="h-10 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                >
                  {allRoles.map((r) => (
                    <option key={r} value={r}>
                      {r === 'All' ? 'All Roles' : r}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <ListFilter className="h-4 w-4 text-gray-500" />
                <select
                  value={statusFilter}
                  onChange={(e) =>
                    setStatusFilter(e.target.value.toLowerCase())
                  }
                  className="h-10 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="text-sm text-gray-500 font-medium">
                {filtered.length} of {team.length} members
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={loadTeam}
                disabled={loading}
                className="inline-flex items-center gap-2 h-10 rounded-lg border border-gray-200 px-4 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <RefreshCcw
                  className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
              <button
                onClick={() => setShowCreate((s) => !s)}
                className="inline-flex items-center gap-2 h-10 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-4 text-sm font-medium text-white hover:from-blue-700 hover:to-indigo-700 transition-all shadow-sm hover:shadow-md"
              >
                <UserPlus className="h-4 w-4" />
                Add Member
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Create Panel */}
      {showCreate && (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <div className="rounded-2xl border border-gray-200 bg-white shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <Plus className="h-4 w-4 text-green-600" />
                  </div>
                  <h2 className="text-lg font-semibold text-gray-900">
                    Add New Team Member
                  </h2>
                </div>
                <button
                  onClick={() => setShowCreate(false)}
                  className="h-8 w-8 grid place-items-center rounded-lg hover:bg-gray-200 transition-colors"
                  title="Close"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <MemberForm
                value={newMember}
                onChange={setNewMember}
                onSubmit={handleCreate}
                submitLabel="Create Member"
              />
            </div>
          </div>
        </div>
      )}

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {loading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-80 rounded-2xl border border-gray-200 bg-white shadow-sm animate-pulse"
              />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-8 text-center">
            <div className="text-red-600 font-medium">{error}</div>
          </div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No team members found
            </h3>
            <p className="text-gray-500">
              Try adjusting your search or filters, or add a new member.
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((m) => {
              const isEditing = !!editMap[m.id]
              return (
                <div
                  key={m.id}
                  className="group rounded-2xl border border-gray-200 bg-white shadow-sm hover:shadow-lg transition-all duration-200 overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="relative bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-100">
                    <div className="flex items-center gap-4">
                      <div className="relative">
                        <div className="h-12 w-12 overflow-hidden rounded-xl bg-gray-200 ring-2 ring-white shadow-sm">
                          {m.image ? (
                            <img
                              src={m.imageURL}
                              alt={m.name}
                              className="h-full w-full object-cover"
                              onError={(e) =>
                                (e.currentTarget.style.display = 'none')
                              }
                            />
                          ) : (
                            <div className="h-full w-full grid place-items-center text-gray-400">
                              <ImageIcon className="h-6 w-6" />
                            </div>
                          )}
                        </div>
                        <div
                          className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white ${
                            (m.status || 'active').toLowerCase() === 'active'
                              ? 'bg-green-400'
                              : 'bg-red-400'
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        {isEditing ? (
                          <input
                            value={editMap[m.id].name}
                            onChange={(e) =>
                              setEditMap((prev) => ({
                                ...prev,
                                [m.id]: {
                                  ...prev[m.id],
                                  name: e.target.value
                                }
                              }))
                            }
                            className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium"
                          />
                        ) : (
                          <div className="font-semibold text-gray-900 truncate">
                            {m.name}
                          </div>
                        )}
                        {/* <div className="text-xs text-gray-500 mt-0.5">
                          ID: <span className="font-mono">{m.id}</span>
                        </div> */}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-4">
                      {isEditing ? (
                        <>
                          <button
                            disabled={savingId === m.id}
                            onClick={() => saveEdit(m.id)}
                            className={classNames(
                              'inline-flex items-center gap-2 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700 transition-colors',
                              savingId === m.id &&
                                'opacity-70 cursor-not-allowed'
                            )}
                          >
                            <Save className="h-3.5 w-3.5" />
                            Save Changes
                          </button>
                          <button
                            onClick={() => cancelEdit(m.id)}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 transition-colors"
                          >
                            <X className="h-3.5 w-3.5" />
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEdit(m)}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium hover:bg-gray-50 transition-colors"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                            Edit
                          </button>
                          <button
                            onClick={() => removeMember(m.id)}
                            className="inline-flex items-center gap-2 rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="p-6 space-y-4">
                    {/* Role Section */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Role
                      </label>
                      {isEditing ? (
                        <input
                          value={editMap[m.id].role}
                          onChange={(e) =>
                            setEditMap((prev) => ({
                              ...prev,
                              [m.id]: { ...prev[m.id], role: e.target.value }
                            }))
                          }
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        />
                      ) : (
                        <div className="font-medium text-gray-900">
                          {m.role}
                        </div>
                      )}

                      <div className="flex flex-wrap gap-2 pt-1">
                        {isEditing ? (
                          <RolesEditor
                            value={editMap[m.id].roles || []}
                            onChange={(roles) =>
                              setEditMap((prev) => ({
                                ...prev,
                                [m.id]: { ...prev[m.id], roles }
                              }))
                            }
                          />
                        ) : (m.roles || []).length ? (
                          m.roles.map((r) => (
                            <span
                              key={r}
                              className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 border border-blue-200"
                            >
                              {r}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400 italic">
                            No roles assigned
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Status Section */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Status
                      </label>
                      {isEditing ? (
                        <select
                          value={editMap[m.id].status || 'active'}
                          onChange={(e) =>
                            setEditMap((prev) => ({
                              ...prev,
                              [m.id]: { ...prev[m.id], status: e.target.value }
                            }))
                          }
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm bg-white"
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      ) : (
                        <span
                          className={classNames(
                            'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                            m.status === 'active'
                              ? 'bg-green-50 text-green-700 border border-green-200'
                              : 'bg-gray-100 text-gray-600 border border-gray-200'
                          )}
                        >
                          {m.status}
                        </span>
                      )}
                    </div>

                    {/* Image URL */}
                    <div className="space-y-2">
                      <div>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                          Profile URL
                        </div>
                        {isEditing ? (
                          <input
                            value={editMap[m.id].image || ''}
                            onChange={(e) =>
                              setEditMap((prev) => ({
                                ...prev,
                                [m.id]: { ...prev[m.id], image: e.target.value }
                              }))
                            }
                            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mt-1"
                            placeholder="sample.webp"
                          />
                        ) : m.image ? (
                          <div className="mt-1">
                            <a
                              href={m.image}
                              className="text-sm text-blue-600 hover:underline break-all"
                              target="_blank"
                              rel="noreferrer"
                            >
                              {m.image.length > 40
                                ? m.image.substring(0, 40) + '...'
                                : m.image}
                            </a>
                          </div>
                        ) : (
                          <div className="mt-1">
                            <span className="text-xs text-gray-400 italic">
                              No image set
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Bio */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Bio
                      </label>
                      {isEditing ? (
                        <textarea
                          rows={3}
                          value={editMap[m.id].bio || ''}
                          onChange={(e) =>
                            setEditMap((prev) => ({
                              ...prev,
                              [m.id]: { ...prev[m.id], bio: e.target.value }
                            }))
                          }
                          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
                        />
                      ) : m.bio ? (
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {m.bio}
                        </p>
                      ) : (
                        <span className="text-xs text-gray-400 italic">
                          No bio provided
                        </span>
                      )}
                    </div>

                    {/* Social Links */}
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Social Links
                      </label>
                      {isEditing ? (
                        <SocialEditor
                          value={editMap[m.id].social || []}
                          onChange={(social) =>
                            setEditMap((prev) => ({
                              ...prev,
                              [m.id]: { ...prev[m.id], social }
                            }))
                          }
                        />
                      ) : (m.social || []).length ? (
                        <div className="flex flex-wrap gap-2">
                          {m.social.map((s, i) => (
                            <a
                              key={i}
                              href={s.url}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 text-sm text-gray-700 hover:bg-gray-100 transition-colors border border-gray-200"
                            >
                              <span className="w-4 h-4 flex items-center justify-center">
                                <i className={classNames('text-xs', s.icon)} />
                              </span>
                              <span className="truncate max-w-20">
                                {s.platform}
                              </span>
                              <Link2 className="h-3 w-3 opacity-50" />
                            </a>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400 italic">
                          No social links
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

/** ---------- Subcomponents ---------- **/

function RolesEditor({ value, onChange }) {
  const [input, setInput] = useState(value.join(', '))
  useEffect(() => {
    setInput(value.join(', '))
  }, [value])
  return (
    <input
      value={input}
      onChange={(e) => {
        setInput(e.target.value)
        const roles = e.target.value
          .split(',')
          .map((r) => r.trim())
          .filter(Boolean)
        onChange(roles)
      }}
      placeholder="e.g. executive, engineering"
      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
    />
  )
}

function SocialEditor({ value, onChange }) {
  const [list, setList] = useState(value)

  useEffect(() => {
    setList(value)
  }, [value])

  function update(idx, patch) {
    const next = list.map((s, i) => (i === idx ? { ...s, ...patch } : s))
    setList(next)
    onChange(next)
  }

  function add() {
    const next = [...list, { platform: '', url: '', icon: '' }]
    setList(next)
    onChange(next)
  }

  function remove(idx) {
    const next = list.filter((_, i) => i !== idx)
    setList(next)
    onChange(next)
  }

  return (
    <div className="space-y-3">
      {list.map((s, i) => (
        <div
          key={i}
          className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2"
        >
          <div className="grid grid-cols-3 gap-2">
            <select
              value={s.platform || ''}
              onChange={(e) => {
                const platform = e.target.value
                update(i, {
                  platform,
                  icon: PLATFORM_ICON_MAP[platform] || s.icon || ''
                })
              }}
              className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm bg-white"
            >
              <option value="">Select Platform</option>
              {SOCIAL_OPTIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
            <input
              value={s.url || ''}
              onChange={(e) => update(i, { url: e.target.value })}
              placeholder="Profile URL"
              className="col-span-2 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm bg-white"
              type="url"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              value={s.icon || ''}
              onChange={(e) => update(i, { icon: e.target.value })}
              className="flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm bg-white"
              placeholder="Icon class (auto-filled)"
            />
            <button
              onClick={() => remove(i)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
              title="Remove"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ))}

      <div className="pt-2">
        <button
          onClick={add}
          className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" /> Add Social Link
        </button>
      </div>
    </div>
  )
}

function MemberForm({ value, onChange, onSubmit, submitLabel = 'Save' }) {
  const [local, setLocal] = useState(value)
  useEffect(() => setLocal(value), [value])

  function patch(p) {
    const next = { ...local, ...p }
    setLocal(next)
    onChange(next)
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Name *</label>
          <input
            value={local.name}
            onChange={(e) => patch({ name: e.target.value })}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Jane Doe"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Role *</label>
          <input
            value={local.role}
            onChange={(e) => patch({ role: e.target.value })}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Lead Engineer"
          />
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Roles</label>
          <input
            value={Array.isArray(local.roles) ? local.roles.join(', ') : ''}
            onChange={(e) =>
              patch({
                roles: e.target.value
                  .split(',')
                  .map((r) => r.trim())
                  .filter(Boolean)
              })
            }
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="executive, engineering"
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <select
            value={local.status || 'active'}
            onChange={(e) => patch({ status: e.target.value })}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Image URL</label>
          <input
            value={local.image}
            onChange={(e) => patch({ image: e.target.value })}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="sample.webp"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Bio</label>
        <textarea
          rows={3}
          value={local.bio}
          onChange={(e) => patch({ bio: e.target.value })}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Brief description..."
        />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">
          Social Links
        </label>
        <SocialEditor
          value={local.social || []}
          onChange={(social) => patch({ social })}
        />
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
        <button
          onClick={onSubmit}
          className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors shadow-sm"
        >
          <Save className="h-4 w-4" /> {submitLabel}
        </button>
      </div>
    </div>
  )
}

export default TeamCrudApp
